import type { Maybe } from '@/frontend-types'
import type { SerializedHeadingNode, SerializedTextNode } from '@payloadcms/richtext-lexical'
import type { SerializedLexicalNode } from '@payloadcms/richtext-lexical/lexical'
import slug from 'slug'
import type { SerializedAllFeaturesEditorState } from '../features'

type NavItem = {
	label: string
	href: string
}

function isSerializedTextNode(node: SerializedLexicalNode): node is SerializedTextNode {
	return node.type === 'text'
}

function isSerializedHeadingNode(node: SerializedLexicalNode): node is SerializedHeadingNode {
	return node.type === 'heading'
}

function getTextContent(
	node: SerializedLexicalNode & { children?: SerializedLexicalNode[] },
): string {
	if (isSerializedTextNode(node)) return node.text
	let textContent = ''
	for (const child of node.children ?? []) {
		textContent += getTextContent(child)
	}
	return textContent
}

export function asHref(text: string) {
	return `#${slug(text)}`
}

export function resolveHeadings(state: Maybe<SerializedAllFeaturesEditorState>, afterIndex = -1) {
	const navItems: NavItem[] = []

	const resolveHeadingSlug = (heading: string) => {
		const href = asHref(heading)

		// If no exact match, return slug as-is
		if (!navItems.some((h) => h.href === href)) return href

		// Otherwise, increment index and append to href
		const index = navItems.reduce((acc, navItem) => {
			if (navItem.href.startsWith(href)) {
				const suffix = navItem.href.replace(`${href}-`, '')
				const intSuffix = Number.parseInt(suffix)
				if (!Number.isNaN(intSuffix) && intSuffix.toString() === suffix && intSuffix >= acc) {
					return intSuffix + 1
				}
			}
			return acc
		}, 1)

		return `${href}-${index}`
	}

	const addNavItem = (label: string) => {
		if (label) {
			const href = resolveHeadingSlug(label)
			return navItems.push({ label, href })
		}
	}

	const parseHeadingNode = (node: SerializedHeadingNode) => {
		const textContent = getTextContent(node)
		addNavItem(textContent)
	}

	const parseNavItemsFromEditorState = (
		state: Maybe<SerializedAllFeaturesEditorState>,
		afterIndex = -1,
	) => {
		for (const node of state?.root.children?.slice(afterIndex + 1) ?? []) {
			switch (node.type) {
				case 'block':
					switch (node.fields.blockType) {
						case 'contrast-section':
							parseNavItemsFromEditorState(node.fields.content)
							break
						case 'subtitle':
							addNavItem(node.fields.text)
							break
						case 'two-column-text': {
							const [firstChildNodeInLeftColumn] = node.fields.left?.root?.children ?? []
							if (
								firstChildNodeInLeftColumn &&
								isSerializedHeadingNode(firstChildNodeInLeftColumn) &&
								firstChildNodeInLeftColumn.tag === 'h2'
							) {
								parseHeadingNode(firstChildNodeInLeftColumn)
							}
							break
						}
						default:
							break
					}
					break
				case 'heading':
					parseHeadingNode(node)
					break
				default:
					break
			}
		}
	}

	parseNavItemsFromEditorState(state, afterIndex)
	return navItems
}
